/**
 * MIDIManager - MIDI Device Connection and Management
 * Handles device detection, connection, and message routing.
 * Emits events instead of manipulating DOM directly.
 */
import { MIDI_CONSTANTS } from './MIDIConstants.js';

export class MIDIManager {
  constructor(state) {
    this.state = state;
    this.midiAccess = null;
    this.midiInput = null;
    this.midiOutput = null;
    this.isConnected = false;
    this.supported = false;
    this._knownInputIds = new Set();
    this._pollInterval = null;

    // Event callbacks - set these from outside
    this.onMessage = null; // (data, rawStatus, channel) => {}
    this.onStatusChange = null; // (message, connected) => {}
    this.onDevicesChange = null; // () => {} — fires when inputs are added or removed

    this.checkMIDISupport();
  }

  checkMIDISupport() {
    this.supported = !!navigator.requestMIDIAccess;
    if (!this.supported) {
      this._emitStatus('Web MIDI API not supported', false);
    }
  }

  async connect() {
    if (!this.supported || this.isConnected) return;

    try {
      if (!this.midiAccess) {
        this.midiAccess = await navigator.requestMIDIAccess({ sysex: false, software: true });
        this._setupStateChangeHandler();
      }

      const inputs = Array.from(this.midiAccess.inputs.values());
      console.log(`[MIDI] inputs found (${inputs.length}):`, inputs.map(i => `"${i.name}"`));

      if (inputs.length === 0) {
        this._emitStatus('No MIDI inputs found', false);
        return;
      }

      this._connectInput(inputs[0]);

    } catch (error) {
      this._emitStatus('MIDI Access denied', false);
    }
  }

  _setupStateChangeHandler() {
    if (!this.midiAccess) return;

    // Snapshot the initial set of known inputs
    this._knownInputIds = new Set(this.midiAccess.inputs.keys());

    this.midiAccess.onstatechange = (event) => {
      const { name, type, state, connection } = event.port;
      console.log(`[MIDI] port state change: "${name}" (${type}) state=${state} connection=${connection}`);
      if (type !== 'input') return;
      if (state === 'connected' && connection === 'closed') {
        // A genuinely new device appeared (not just a port being opened/closed)
        setTimeout(() => { if (this.onDevicesChange) this.onDevicesChange(); }, 0);
      } else if (state === 'disconnected') {
        if (this.midiInput && event.port.id === this.midiInput.id) {
          this.disconnect();
        }
        if (this.onDevicesChange) this.onDevicesChange();
      }
    };

    // Fallback poll — onstatechange is unreliable on some OS/driver combos
    this._pollInterval = setInterval(() => this._pollInputs(), 2000);
  }

  _pollInputs() {
    if (!this.midiAccess) return;
    const currentIds = new Set(this.midiAccess.inputs.keys());
    const changed =
      currentIds.size !== this._knownInputIds.size ||
      [...currentIds].some(id => !this._knownInputIds.has(id));
    if (changed) {
      console.log('[MIDI] poll detected device change, refreshing list');
      this._knownInputIds = currentIds;
      if (this.onDevicesChange) this.onDevicesChange();
    }
  }

  _connectInput(input) {
    this.midiInput = input;
    this.midiInput.onmidimessage = (event) => this._handleMessage(event);
    this.isConnected = true;
    this._emitStatus(`Connected: ${input.name}`, true);
    this.state.set('midiConnected', true);
    this.state.set('midiDeviceName', input.name);
  }

  disconnect() {
    if (this.midiInput) {
      this.midiInput.onmidimessage = null;
      this.midiInput = null;
    }
    this.midiOutput = null;
    // Keep midiAccess so hot-plug detection and device enumeration continue working
    this.isConnected = false;
    this._emitStatus('Disconnected', false);
    this.state.set('midiConnected', false);
    this.state.set('midiDeviceName', null);
  }

  destroy() {
    this.disconnect();
    if (this._pollInterval) {
      clearInterval(this._pollInterval);
      this._pollInterval = null;
    }
  }

  _handleMessage(event) {
    const data = event.data;
    if (this.onMessage) {
      this.onMessage(data);
    }
  }

  _emitStatus(message, connected) {
    if (this.onStatusChange) {
      this.onStatusChange(message, connected);
    }
    // Also update DOM status indicator if it exists
    this._updateStatusDOM(message, connected);
  }

  _updateStatusDOM(message, connected) {
    const el = document.getElementById('midi-status');
    if (!el) return;
    const dot = el.querySelector('div.rounded-full');
    const text = el.querySelector('span');
    if (dot) {
      dot.classList.remove('bg-red-500', 'bg-green-500');
      dot.classList.add(connected ? 'bg-green-500' : 'bg-red-500');
    }
    if (text) {
      text.textContent = message;
    }
  }

  getAvailableInputs() {
    if (!this.midiAccess) return [];
    return Array.from(this.midiAccess.inputs.values());
  }

  connectToInput(inputName) {
    const inputs = this.getAvailableInputs();
    const input = inputs.find(i => i.name === inputName);
    if (input) {
      if (this.midiInput) this.midiInput.onmidimessage = null;
      this._connectInput(input);
      return true;
    }
    return false;
  }

  async refreshDevices() {
    if (!this.midiAccess) {
      try {
        this.midiAccess = await navigator.requestMIDIAccess({ sysex: false, software: true });
        this._setupStateChangeHandler();
      } catch (e) {
        this._emitStatus('MIDI Access denied', false);
        return;
      }
    }
    const inputs = Array.from(this.midiAccess.inputs.values());
    console.log(`[MIDI] refresh — inputs (${inputs.length}):`, inputs.map(i => `"${i.name}"`));
    // Update known IDs so the poll doesn't fire redundantly
    this._knownInputIds = new Set(this.midiAccess.inputs.keys());
    if (this.onDevicesChange) this.onDevicesChange();
  }

  getCurrentDeviceInfo() {
    return {
      input: this.midiInput ? this.midiInput.name : 'None',
      output: this.midiOutput ? this.midiOutput.name : 'None'
    };
  }

  sendMIDI(data) {
    if (this.midiOutput) this.midiOutput.send(data);
  }

  sendNoteOn(note, velocity, channel = 0) {
    this.sendMIDI([MIDI_CONSTANTS.messageTypes.noteOn + channel, note, velocity]);
  }

  sendNoteOff(note, velocity, channel = 0) {
    this.sendMIDI([MIDI_CONSTANTS.messageTypes.noteOff + channel, note, velocity]);
  }

  sendControlChange(controller, value, channel = 0) {
    this.sendMIDI([MIDI_CONSTANTS.messageTypes.controlChange + channel, controller, value]);
  }
}
